import { useState } from "react";
import { Wine, Users, Calendar, GlassWater, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WineTasting = () => {
  const [showReserveForm, setShowReserveForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    guests: 2,
    notify_future: true,
  });

  const features = [
    { icon: Wine, text: "Descubre nuevas bodegas" },
    { icon: GlassWater, text: "Aprende sobre maridajes" },
    { icon: Users, text: "Degusta vinos exclusivos" },
    { icon: Calendar, text: "Catas mensuales" },
  ];

  const handleReserve = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/wine-tasting/reserve`, formData);
      if (response.data.success) {
        toast.success(response.data.message);
        setShowReserveForm(false);
        setFormData({
          name: "",
          email: "",
          phone: "",
          guests: 2,
          notify_future: true,
        });
      }
    } catch (error) {
      toast.error("Error al realizar la reserva. Por favor, inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!notifyEmail) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/wine-tasting/notify`, {
        email: notifyEmail,
      });
      if (response.data.success) {
        toast.success(response.data.message);
        setNotifyEmail("");
      }
    } catch (error) {
      toast.error("Error al suscribirse. Por favor, inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="catas"
      className="relative py-24 md:py-32"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1763750416411-76f27819558d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHx3aW5lJTIwY2VsbGFyJTIwYmFycmVscyUyMHdvb2RlbnxlbnwwfHx8fDE3NzMzNDc0NDh8MA&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-[#1A1A1A]/85" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C5A059]/20 border border-[#C5A059]/40 mb-6">
              <Wine size={16} className="text-[#C5A059]" />
              <span className="font-body text-sm uppercase tracking-widest text-[#C5A059]">
                Experiencia exclusiva
              </span>
            </div>

            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl mb-6">
              Catas de Vino en Sevilla Este
            </h2>

            <p className="font-body text-white/80 leading-relaxed mb-6">
              Una vez al mes organizamos catas de vino dirigidas por profesionales,
              en colaboración con nuestras bodegas proveedoras.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3"
                  data-testid={`tasting-feature-${index}`}
                >
                  <div className="w-10 h-10 flex items-center justify-center border border-[#C5A059]/40">
                    <feature.icon size={18} className="text-[#C5A059]" />
                  </div>
                  <span className="font-body text-sm text-white/80">
                    {feature.text}
                  </span>
                </div>
              ))}
            </div>

            <p className="font-accent text-xl text-[#C5A059] mb-8">
              Plazas limitadas
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setShowReserveForm(true)}
                data-testid="reserve-tasting-btn"
                className="btn-primary bg-[#C5A059] hover:bg-[#B08D4A]"
              >
                Reservar Plaza
              </button>
            </div>
          </div>

          {/* Form or Notify */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8">
            {showReserveForm ? (
              <form onSubmit={handleReserve} className="space-y-6">
                <h3 className="font-heading text-2xl text-white mb-6">
                  Reserva tu plaza
                </h3>

                <div>
                  <input
                    type="text"
                    placeholder="Nombre completo"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    data-testid="tasting-name-input"
                    className="w-full bg-transparent border-b border-white/30 text-white placeholder:text-white/50 py-3 focus:outline-none focus:border-[#C5A059]"
                  />
                </div>

                <div>
                  <input
                    type="email"
                    placeholder="Email"
                    required
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    data-testid="tasting-email-input"
                    className="w-full bg-transparent border-b border-white/30 text-white placeholder:text-white/50 py-3 focus:outline-none focus:border-[#C5A059]"
                  />
                </div>

                <div>
                  <input
                    type="tel"
                    placeholder="Teléfono"
                    required
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    data-testid="tasting-phone-input"
                    className="w-full bg-transparent border-b border-white/30 text-white placeholder:text-white/50 py-3 focus:outline-none focus:border-[#C5A059]"
                  />
                </div>

                <div>
                  <label className="font-body text-sm text-white/70 mb-2 block">
                    Número de personas
                  </label>
                  <select
                    value={formData.guests}
                    onChange={(e) =>
                      setFormData({ ...formData, guests: parseInt(e.target.value) })
                    }
                    data-testid="tasting-guests-select"
                    className="w-full bg-transparent border-b border-white/30 text-white py-3 focus:outline-none focus:border-[#C5A059]"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                      <option key={num} value={num} className="bg-[#1A1A1A]">
                        {num} {num === 1 ? "persona" : "personas"}
                      </option>
                    ))}
                  </select>
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.notify_future}
                    onChange={(e) =>
                      setFormData({ ...formData, notify_future: e.target.checked })
                    }
                    className="w-5 h-5 border-white/30 bg-transparent"
                  />
                  <span className="font-body text-sm text-white/70">
                    Avisarme de próximas catas
                  </span>
                </label>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-tasting-btn"
                    className="btn-primary bg-[#C5A059] hover:bg-[#B08D4A] flex-1"
                  >
                    {loading ? "Enviando..." : "Confirmar Reserva"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowReserveForm(false)}
                    className="btn-secondary border-white/30 text-white hover:bg-white/10"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <h3 className="font-heading text-2xl text-white mb-4">
                  Avísame de próximas catas
                </h3>
                <p className="font-body text-white/70 mb-6">
                  Déjanos tu email y te informaremos de nuestras próximas catas de vino.
                </p>
                <form onSubmit={handleNotify} className="space-y-4">
                  <input
                    type="email"
                    placeholder="Tu email"
                    required
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    data-testid="notify-email-input"
                    className="w-full bg-transparent border-b border-white/30 text-white placeholder:text-white/50 py-3 focus:outline-none focus:border-[#C5A059]"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="notify-submit-btn"
                    className="btn-secondary border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white w-full"
                  >
                    {loading ? "Suscribiendo..." : "Avisarme"}
                  </button>
                </form>

                <div className="mt-8 pt-6 border-t border-white/10">
                  <div className="flex items-center gap-2 text-white/60">
                    <Check size={16} className="text-[#C5A059]" />
                    <span className="font-body text-sm">Sin spam, solo catas</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WineTasting;
